import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ExperienceBookingDetails.css';
import { db } from '../../config/firebaseConfig';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { HiOutlineClipboardDocumentList, HiOutlineInformationCircle, HiOutlineCreditCard, HiOutlinePhone, HiOutlineCurrencyEuro, HiOutlineCalendarDays, HiOutlineUserGroup, HiOutlineMapPin, HiOutlineShieldCheck, HiOutlineArrowLeft, HiOutlineWallet } from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import axios from 'axios';

const ExperienceBookingDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const bookingData = location.state;
    const { currentUser, userData, affiliateTier } = useAuth();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
        cardHolderName: '',
        cardNumber: '',
        expiryDate: '',
        cvc: ''
    });
    const [contactInfo, setContactInfo] = useState({
        phoneNumber: '',
        whatsappNumber: '',
        usePhoneForWhatsapp: true
    });
    const [paymentMethod, setPaymentMethod] = useState('Stripe');
    const [useBalance, setUseBalance] = useState(false);
    const [availableBalance, setAvailableBalance] = useState(0);
    const [balanceLoading, setBalanceLoading] = useState(true);
    
    const auth = getAuth();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        setContactInfo({
                            phoneNumber: data.phoneNumber || data.phone || '',
                            whatsappNumber: data.whatsappNumber || '',
                            usePhoneForWhatsapp: data.usePhoneForWhatsapp !== false
                        });
                    }
                } catch (error) {
                    console.error('Error fetching user profile:', error);
                }
            }
        };

        fetchUserProfile();
    }, [currentUser]);

    // Fetch affiliate balance (same logic as EarningsReport)
    useEffect(() => {
        const fetchBalance = async () => {
            if (!currentUser) { setBalanceLoading(false); return; }
            try {
                const now = dayjs();

                // Fetch yacht bookings
                const yachtSnap = await getDocs(
                    query(collection(db, 'bookings'), where('affiliateId', '==', currentUser.uid))
                );
                const yachtBookings = yachtSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                // Fetch experience bookings
                const expSnap = await getDocs(
                    query(collection(db, 'experienceBookings'), where('affiliateId', '==', currentUser.uid))
                );
                const experienceBookings = expSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(b => b.paymentStatus !== 'pending' && b.paymentStatus !== 'failed' && b.status !== 'cancelled');

                const allBookings = [...yachtBookings, ...experienceBookings];

                // Raw commission balance: past bookings, not yet paid out
                const rawBalance = allBookings
                    .filter(b => b.date < now.format('YYYY-MM-DD') && !b.affiliatePaid)
                    .reduce((sum, b) => sum + (b.commission || 0), 0);

                // Subtract previous balance deductions
                const deductionsSnap = await getDocs(
                    query(collection(db, 'balanceDeductions'), where('affiliateId', '==', currentUser.uid))
                );
                const totalDeductions = deductionsSnap.docs
                    .reduce((sum, d) => sum + (d.data().amount || 0), 0);

                // Subtract withdraw request amounts
                const withdrawSnap = await getDocs(
                    query(collection(db, 'withdrawRequests'), where('affiliateId', '==', currentUser.uid))
                );
                const totalWithdrawn = withdrawSnap.docs
                    .reduce((sum, d) => sum + (d.data().balanceAmount || 0), 0);

                setAvailableBalance(Math.max(0, rawBalance - totalDeductions - totalWithdrawn));
            } catch (error) {
                console.error('Error fetching balance:', error);
            } finally {
                setBalanceLoading(false);
            }
        };
        fetchBalance();
    }, [currentUser]);

    if (!bookingData) {
        return (
            <div className="exp-booking-page">
                <div className="exp-empty-state">
                    <h2>No booking data available</h2>
                    <button onClick={() => navigate('/experiences')} className="exp-back-btn">
                        <HiOutlineArrowLeft />
                        Back to Experiences
                    </button>
                </div>
            </div>
        );
    }

    const handlePayment = async () => {
        if (remainingToPay > 0) {
            if (paymentMethod === '3DSecure') {
                if (!paymentDetails.cardHolderName) {
                    alert('Please provide card holder name');
                    return;
                }
                if (!paymentDetails.cardNumber) {
                    alert('Please provide card number');
                    return;
                }
                if (!paymentDetails.expiryDate) {
                    alert('Please provide expiry date');
                    return;
                }
                if (!paymentDetails.cvc) {
                    alert('Please provide CVC');
                    return;
                }
            }
        }

        if (!contactInfo.phoneNumber) {
            alert('Please provide your phone number');
            return;
        }

        if (!contactInfo.usePhoneForWhatsapp && !contactInfo.whatsappNumber) {
            alert('Please provide your WhatsApp number');
            return;
        }

        if (!termsAccepted) {
            alert('Please accept the terms and conditions');
            return;
        }

        if (!currentUser) {
            alert('Please sign in to make a booking');
            navigate('/login');
            return;
        }

        setIsProcessing(true);

        try {
            // Calculate end time based on start time and duration
            // Build date in Turkey timezone (UTC+3) so it is correct regardless of user's local timezone
            const startTimeFormatted = bookingData.option.startTime.length === 5 ? `${bookingData.option.startTime}:00` : bookingData.option.startTime;
            const startDate = new Date(`${bookingData.date}T${startTimeFormatted}+03:00`);
            const totalDurationMinutes = (bookingData.option.duration * 60) + (bookingData.option.durationMinutes || 0);
            const endDate = new Date(startDate.getTime() + (totalDurationMinutes * 60 * 1000));
            // Format endTime in Turkey timezone (UTC+3)
            const endDateTurkey = new Date(endDate.getTime() + (3 * 60 * 60 * 1000));
            const endTime = `${endDateTurkey.getUTCHours().toString().padStart(2, '0')}:${endDateTurkey.getUTCMinutes().toString().padStart(2, '0')}`;

            // Generate a unique order ID
            const timestamp = new Date().getTime();
            const randomStr = Math.random().toString(36).substring(2, 10);
            const orderId = `AFF-EXP-${timestamp}-${randomStr}`;

            // Create the experience booking document
            const bookingRef = await addDoc(collection(db, 'experienceBookings'), {
                experienceId: bookingData.experienceId,
                experienceName: bookingData.experienceName,
                experienceImage: bookingData.experienceImage,
                hostId: bookingData.hostId,
                hostName: bookingData.hostName,
                hostPhone: bookingData.hostPhone,
                optionId: bookingData.option.id,
                optionName: bookingData.option.name,
                optionType: bookingData.option.type,
                userId: currentUser.uid,
                userEmail: currentUser.email,
                phoneNumber: contactInfo.phoneNumber,
                whatsappNumber: contactInfo.usePhoneForWhatsapp ? contactInfo.phoneNumber : contactInfo.whatsappNumber,
                date: bookingData.date,
                startTime: bookingData.option.startTime,
                endTime: endTime,
                duration: bookingData.option.duration,
                durationMinutes: bookingData.option.durationMinutes || 0,
                guestCount: bookingData.guestCount,
                meetingPoint: bookingData.meetingPoint,
                meetingPointDescription: bookingData.meetingPointDescription,
                googleMapsLink: bookingData.googleMapsLink,
                priceBreakdown: {
                    basePrice: bookingData.regularPrice,
                    affiliatePrice: bookingData.totalPrice,
                    finalPrice: bookingData.totalPrice,
                    discount: bookingData.discount || 0,
                    balanceUsed: balanceToUse,
                    amountCharged: remainingToPay
                },
                customerName: userData?.companyName || userData?.fullName || currentUser.email,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isAffiliateBooking: true,
                affiliateId: "",
                affiliateTier: affiliateTier,
                affiliateEmail: currentUser.email,
                affiliateName: userData?.companyName || userData?.fullName || currentUser.email,
                bookingFromAffiliateWebsite: true,
                orderId: orderId,
                balanceUsed: balanceToUse,
                paymentStatus: (isFreeBooking || isFullyCoveredByBalance) ? 'completed' : 'pending',
                status: (isFreeBooking || isFullyCoveredByBalance) ? 'confirmed' : 'cancelled',
                cancellationReason: (isFreeBooking || isFullyCoveredByBalance) ? null : 'Booking not paid yet'
            });

            const bookingId = bookingRef.id;

            // Record balance deduction if balance was used
            if (balanceToUse > 0) {
                await addDoc(collection(db, 'balanceDeductions'), {
                    affiliateId: currentUser.uid,
                    affiliateEmail: currentUser.email,
                    amount: balanceToUse,
                    bookingId: bookingId,
                    bookingType: 'experience',
                    experienceName: bookingData.experienceName,
                    description: `Balance used for ${bookingData.experienceName}`,
                    createdAt: serverTimestamp()
                });
            }

            // If it's a free booking or fully covered by balance, redirect to confirmation
            if (isFreeBooking || isFullyCoveredByBalance) {

                try {
                    const response = await axios.post('https://bosp-yachts-backend.vercel.app/api/mailchimp/send-email', {
                            templateName: 'exp-usr-experience-booking-confirmation-new-26',
                            email: currentUser.email,
                            firstName: userData.firstName || currentUser.email,
                            lastName: userData.lastName || currentUser.email,
                            emailVars: [
                            {
                                name: 'guestName',
                                content: userData.firstName + ' ' + userData.lastName || currentUser.email
                            },
                            {
                                name: 'experienceName',
                                content: bookingData.name
                            },
                            {
                                name: 'reservationNumber',
                                content: bookingId
                            },
                            {
                                name: 'guestNumber',
                                content: bookingData.guestCount + ' people'
                            },
                            {
                                name: 'bookingDate',
                                content: new Date(bookingData.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })
                            },
                            {
                                name: 'bookingTime',
                                content: bookingData.option.startTime
                            },
                            {
                                name: 'duration',
                                content: bookingData.option.duration + 'h' + bookingData.option.durationMinutes ?  bookingData.option.durationMinutes + 'm' : ''
                            },
                            {
                                name: 'mapsLink',
                                content: bookingData.googleMapsLink
                            },
                            {
                                name: 'hostName',
                                content: bookingData.hostName 
                            },
                            {
                                name: 'hostPhone',
                                content: bookingData.hostPhone
                            },
                            {
                                name: 'totalPrice',
                                content: "FREE"
                            },
                            {
                                name: 'discountCode',
                                content: bookingData.discountCode || 'No Discount Code'
                            }
                            ]
                    });

                    const response2 = await axios.post('https://bosp-yachts-backend.vercel.app/api/mailchimp/send-email', {
                            templateName: 'exp-host-booking-received-confirmation-new-26',
                            email: currentUser.email,
                            firstName: userData.firstName || currentUser.email,
                            lastName: userData.lastName || currentUser.email,
                            emailVars: [
                            {
                                name: 'hostName',
                                content: bookingData.hostName 
                            },
                            {
                                name: 'experienceName',
                                content: bookingData.name
                            },
                            {
                                name: 'bookingDate',
                                content: new Date(bookingData.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })
                            },
                            {
                                name: 'bookingTime',
                                content: bookingData.option.startTime
                            },
                            {
                                name: 'duration',
                                content: bookingData.option.duration + 'h' + bookingData.option.durationMinutes ?  bookingData.option.durationMinutes + 'm' : ''
                            },
                            {
                                name: 'mapsLink',
                                content: bookingData.googleMapsLink
                            },
                            {
                                name: 'guestName',
                                content: userData.firstName + ' ' + userData.lastName || userData.email
                            },
                            {
                                name: 'guestNumber',
                                content: bookingData.guestCount + ' people'
                            },
                            {
                                name: 'guestPhone',
                                content: contactInfo.phoneNumber
                            },
                            {
                                name: 'whatsappNumber',
                                content: contactInfo.usePhoneForWhatsapp ? contactInfo.phoneNumber : contactInfo.whatsappNumber
                            },
                            {
                                name: 'guestMail',
                                content: userData.email
                            },
                            {
                                name: 'reservationNumber',
                                content: bookingId
                            },
                            {
                                name: 'hostAmount',
                                content: (bookingData.totalPrice * 2/3).toFixed(2)
                            },
                            ]
                    });

                    const response3 = await axios.post('https://bosp-yachts-backend.vercel.app/api/mailchimp/send-email', {
                                templateName: 'adm-experience-booking-received-new-26',
                                email: "info@bosphorusyachts.com",
                                firstName: "Bosphorus",
                                lastName: "Yachts",
                                emailVars: [
                                {
                                    name: 'experienceName',
                                    content: bookingData.experienceName
                                },
                                {
                                    name: 'bookingDate',
                                    content: new Date(bookingData.date).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })
                                },
                                {
                                    name: 'bookingTime',
                                    content: bookingData.option.startTime
                                },
                                {
                                    name: 'mapsLink',
                                    content: bookingData.googleMapsLink
                                },
                                {
                                    name: 'hostName',
                                    content: bookingData.hostName
                                },
                                {
                                    name: 'hostAmount',
                                    content: (bookingData.totalPrice * 2/3).toFixed(2)
                                },
                                {
                                    name: 'guestName',
                                    content: userData.firstName + ' ' + userData.lastName || userData.email
                                },
                                {
                                    name: 'guestNumber',
                                    content: bookingData.guestCount + ' people'
                                },
                                {
                                    name: 'guestPhone',
                                    content: contactInfo.phoneNumber
                                },
                                {
                                    name: 'whatsappNumber',
                                    content: contactInfo.usePhoneForWhatsapp ? contactInfo.phoneNumber : contactInfo.whatsappNumber
                                },
                                {
                                    name: 'guestMail',
                                    content: userData.email
                                },
                                {
                                    name: 'reservationNumber',
                                    content: bookingId
                                },
                                {
                                    name: 'totalPrice',
                                    content: (bookingData.totalPrice).toFixed(2)
                                },
                                {
                                    name: 'discountCode',
                                    content: bookingData.discountCode
                                },
                                {
                                    name: 'affiliateName',
                                    content: ""
                                }
                                ]
                    });
                } catch (error) {
                    console.error("Error while sending mails")
                }


                navigate(`/experience-booking-confirmation/${orderId}?status=success`, {
                    state: {
                        bookingData: {
                            ...bookingData,
                            id: bookingId,
                            experienceId: bookingData.experienceId,
                            experienceName: bookingData.experienceName,
                            experienceImage: bookingData.experienceImage,
                            optionName: bookingData.option?.name,
                            optionType: bookingData.option?.type,
                            startTime: bookingData.option?.startTime,
                            endTime: bookingData.option?.endTime,
                            duration: bookingData.option?.duration,
                            durationMinutes: bookingData.option?.durationMinutes,
                            date: bookingData.date,
                            guestCount: bookingData.guestCount,
                            hostName: bookingData.hostName,
                            hostPhone: bookingData.hostPhone,
                            meetingPoint: bookingData.meetingPoint,
                            meetingPointDescription: bookingData.meetingPointDescription,
                            googleMapsLink: bookingData.googleMapsLink,
                            priceBreakdown: {
                                basePrice: bookingData.regularPrice,
                                affiliatePrice: bookingData.totalPrice,
                                finalPrice: bookingData.totalPrice,
                                discount: bookingData.discount || 0,
                                balanceUsed: balanceToUse,
                                amountCharged: remainingToPay
                            }
                        }
                    }
                });
                return;
            }

            try {
                if (paymentMethod === 'Stripe') {
                    const response = await fetch('https://bosp-yachts-backend.vercel.app/api/bookings/experience-create-checkout-session', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${await currentUser.getIdToken()}`
                        },
                        body: JSON.stringify({
                            orderId: orderId,
                            bookingData: {
                                ...bookingData,
                                bookingId: bookingRef.id,
                                experienceName: bookingData.experienceName,
                                experienceImage: bookingData.experienceImage,
                                isExperienceBooking: true,
                                isAffiliateBooking: true,
                                balanceUsed: balanceToUse,
                                priceBreakdown: {
                                    finalPrice: remainingToPay,
                                    onlinePayment: remainingToPay
                                }
                            },
                            user: {
                                uid: currentUser.uid,
                                email: currentUser.email,
                                displayName: userData?.companyName || userData?.fullName
                            }
                        })
                    });
                    
                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error('Failed to create checkout session: ' + errorText);
                    }

                    const { id: sessionId, url: checkoutUrl } = await response.json();
                    
                    await updateDoc(doc(db, 'experienceBookings', bookingId), {
                        stripeSessionId: sessionId,
                        updatedAt: new Date().toISOString()
                    });

                    window.location.href = checkoutUrl;
                } else {
                    const response = await fetch('https://bosp-yachts-backend.vercel.app/api/bookings/experience-create-checkout-session-garanti', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${await currentUser.getIdToken()}`
                        },
                        body: JSON.stringify({
                            orderId: orderId,
                            bookingData: {
                                ...bookingData,
                                bookingId: bookingRef.id,
                                experienceName: bookingData.experienceName,
                                experienceImage: bookingData.experienceImage,
                                isExperienceBooking: true,
                                isAffiliateBooking: true,
                                balanceUsed: balanceToUse,
                                priceBreakdown: {
                                    finalPrice: remainingToPay,
                                    onlinePayment: remainingToPay
                                }
                            },
                            user: {
                                uid: currentUser.uid,
                                email: currentUser.email,
                                displayName: userData?.companyName || userData?.fullName
                            },
                            paymentDetails: {
                                cardNumber: paymentDetails.cardNumber,
                                expiryDate: paymentDetails.expiryDate,
                                cvc: paymentDetails.cvc,
                                cardholderName: paymentDetails.cardHolderName
                            }
                        })
                    });

                    const html = await response.text();
                    const win = window.open('', '_self');
                    win.document.open();
                    win.document.write(html);
                    win.document.close();
                }
                
                return;
            } catch (error) {
                console.error('Error during payment process:', error);
                alert(`Payment processing error: ${error.message}`);
                try {
                    await deleteDoc(doc(db, 'experienceBookings', bookingId));
                } catch (deleteError) {
                    console.error('Error deleting booking:', deleteError);
                }
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Error creating booking:', error);
            alert('Failed to create booking. Please try again.');
            setIsProcessing(false);
        }
    };

    const formatDate = (dateString) => {
        // Force Turkey timezone (UTC+3) to prevent date shift in other timezones
        const date = new Date(`${dateString}T00:00:00+03:00`);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'Europe/Istanbul'
        });
    };

    const isFreeBooking = bookingData.totalPrice === 0;
    const balanceToUse = useBalance ? Math.min(availableBalance, bookingData.totalPrice) : 0;
    const remainingToPay = parseFloat((bookingData.totalPrice - balanceToUse).toFixed(2));
    const isFullyCoveredByBalance = useBalance && remainingToPay === 0;

    return (
        <div className="exp-booking-page">
            <div className="exp-booking-wrapper">
                <div className="exp-booking-card">
                    {/* Header */}
                    <div className="exp-booking-header">
                        <HiOutlineClipboardDocumentList />
                        <h1>Affiliate Booking Summary</h1>
                    </div>

                    {/* Tier Badge */}
                    <div className="exp-tier-badge">
                        <span className="tier-tag">
                            <HiOutlineShieldCheck />
                            Tier {affiliateTier} Affiliate Pricing
                        </span>
                    </div>

                    <div className="exp-booking-body">
                        {/* Experience Details */}
                        <div className="exp-section">
                            <h3 className="exp-section-title">
                                <HiOutlineInformationCircle />
                                Experience Details
                            </h3>
                            <div className="exp-detail-header">
                                {bookingData.experienceImage && (
                                    <img 
                                        src={bookingData.experienceImage} 
                                        alt={bookingData.experienceName}
                                        className="exp-detail-thumb"
                                    />
                                )}
                                <div className="exp-detail-info">
                                    <div className="exp-row">
                                        <span className="exp-row-label">Experience</span>
                                        <span className="exp-row-value">{bookingData.experienceName}</span>
                                    </div>
                                    <div className="exp-row">
                                        <span className="exp-row-label">Option</span>
                                        <span className="exp-row-value">{bookingData.option.name} ({bookingData.option.type})</span>
                                    </div>
                                    <div className="exp-row">
                                        <span className="exp-row-label">Host</span>
                                        <span className="exp-row-value">{bookingData.hostName}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trip Details */}
                        <div className="exp-section">
                            <h3 className="exp-section-title">
                                <HiOutlineCalendarDays />
                                Trip Details
                            </h3>
                            <div className="exp-row">
                                <span className="exp-row-label">Date</span>
                                <span className="exp-row-value">{formatDate(bookingData.date)}</span>
                            </div>
                            <div className="exp-row">
                                <span className="exp-row-label">Time</span>
                                <span className="exp-row-value">{bookingData.option.startTime}</span>
                            </div>
                            <div className="exp-row">
                                <span className="exp-row-label">Duration</span>
                                <span className="exp-row-value">{bookingData.option.duration}h{bookingData.option.durationMinutes ? ` ${bookingData.option.durationMinutes}m` : ''}</span>
                            </div>
                            <div className="exp-row">
                                <span className="exp-row-label">Guests</span>
                                <span className="exp-row-value">{bookingData.guestCount} {bookingData.guestCount === 1 ? 'person' : 'people'}</span>
                            </div>
                            {bookingData.meetingPoint && (
                                <div className="exp-row">
                                    <span className="exp-row-label">Meeting Point</span>
                                    <span className="exp-row-value">{bookingData.meetingPoint}</span>
                                </div>
                            )}
                            <div className="exp-row note">
                                <span className="exp-row-label">
                                    <HiOutlineMapPin />
                                    Exact meeting point details will be shared after booking confirmation
                                </span>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className={`exp-section ${!contactInfo.phoneNumber || (!contactInfo.usePhoneForWhatsapp && !contactInfo.whatsappNumber) ? 'error' : ''}`}>
                            <h3 className="exp-section-title">
                                <HiOutlinePhone />
                                Contact Information
                            </h3>
                            <div className="exp-row">
                                <span className="exp-row-label">Phone Number *</span>
                                <span className="exp-row-value">
                                    <input
                                        type="tel"
                                        value={contactInfo.phoneNumber}
                                        onChange={(e) => setContactInfo(prev => ({
                                            ...prev,
                                            phoneNumber: e.target.value,
                                            whatsappNumber: prev.usePhoneForWhatsapp ? e.target.value : prev.whatsappNumber
                                        }))}
                                        placeholder="+90 555 123 4567"
                                        className="exp-contact-input"
                                    />
                                </span>
                            </div>

                            <div className="exp-checkbox-row">
                                <input
                                    type="checkbox"
                                    id="usePhoneForWhatsapp"
                                    checked={contactInfo.usePhoneForWhatsapp}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setContactInfo(prev => ({
                                            ...prev,
                                            usePhoneForWhatsapp: checked,
                                            whatsappNumber: checked ? prev.phoneNumber : prev.whatsappNumber
                                        }));
                                    }}
                                />
                                <label htmlFor="usePhoneForWhatsapp">WhatsApp same as phone number</label>
                            </div>

                            {!contactInfo.usePhoneForWhatsapp && (
                                <div className="exp-row">
                                    <span className="exp-row-label">WhatsApp Number *</span>
                                    <span className="exp-row-value">
                                        <input
                                            type="tel"
                                            value={contactInfo.whatsappNumber}
                                            onChange={(e) => setContactInfo(prev => ({
                                                ...prev,
                                                whatsappNumber: e.target.value
                                            }))}
                                            placeholder="+90 555 123 4567"
                                            className="exp-contact-input"
                                        />
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Price Summary */}
                        <div className="exp-section">
                            <h3 className="exp-section-title">
                                <HiOutlineCurrencyEuro />
                                Price Summary
                            </h3>
                            {bookingData.regularPrice !== bookingData.totalPrice && (
                                <div className="exp-row">
                                    <span className="exp-row-label">Regular Price</span>
                                    <span className="exp-row-value strikethrough">
                                        €{bookingData.regularPrice?.toFixed(2)}
                                    </span>
                                </div>
                            )}
                            <div className="exp-row">
                                <span className="exp-row-label">Affiliate Price (Tier {affiliateTier})</span>
                                <span className="exp-row-value affiliate-price">
                                    {isFreeBooking ? 'FREE' : `€${bookingData.totalPrice.toFixed(2)}`}
                                </span>
                            </div>
                            {bookingData.discount > 0 && (
                                <div className="exp-row">
                                    <span className="exp-row-label">You Save</span>
                                    <span className="exp-row-value savings">
                                        €{(bookingData.regularPrice - bookingData.totalPrice).toFixed(2)} ({bookingData.discount}% off)
                                    </span>
                                </div>
                            )}

                            {/* Balance Usage Toggle */}
                            {!isFreeBooking && !balanceLoading && availableBalance > 0 && (
                                <div className="exp-balance-section">
                                    <div className="exp-balance-toggle" onClick={() => setUseBalance(!useBalance)}>
                                        <div className="exp-balance-check">
                                            <input
                                                type="checkbox"
                                                checked={useBalance}
                                                onChange={(e) => setUseBalance(e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="exp-balance-info">
                                            <HiOutlineWallet />
                                            <div className="exp-balance-text">
                                                <span className="exp-balance-title">Use my balance</span>
                                                <span className="exp-balance-amount">€{availableBalance.toFixed(2)} available</span>
                                            </div>
                                        </div>
                                    </div>
                                    {useBalance && (
                                        <div className="exp-row">
                                            <span className="exp-row-label">Balance Applied</span>
                                            <span className="exp-row-value savings">-€{balanceToUse.toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="exp-row total">
                                <span className="exp-row-label">{useBalance && balanceToUse > 0 ? 'Remaining to Pay' : 'Total to Pay'}</span>
                                <span className="exp-row-value">
                                    {isFreeBooking || isFullyCoveredByBalance
                                        ? 'FREE'
                                        : `€${remainingToPay.toFixed(2)}`}
                                </span>
                            </div>
                        </div>

                        {/* Payment Method & Details */}
                        {!isFreeBooking && remainingToPay > 0 && (
                            <>
                                <div className="exp-section">
                                    <h3 className="exp-section-title">
                                        <HiOutlineCreditCard />
                                        Payment Method
                                    </h3>
                                    <div className="exp-payment-methods">
                                        {/* <div 
                                            className={`exp-payment-option ${paymentMethod === '3DSecure' ? 'selected' : ''}`}
                                            onClick={() => setPaymentMethod('3DSecure')}
                                        >
                                            <div className="exp-radio-circle">
                                                {paymentMethod === '3DSecure' && <div className="exp-radio-dot"></div>}
                                            </div>
                                            <div className="exp-payment-info">
                                                <div className="exp-payment-text">
                                                    <h4>Payment using 3D Secure</h4>
                                                    <p>Secure payment with 3D verification</p>
                                                </div>
                                            </div>
                                        </div> */}
                                        
                                        <div 
                                            className={`exp-payment-option ${paymentMethod === 'Stripe' ? 'selected' : ''}`}
                                            onClick={() => setPaymentMethod('Stripe')}
                                        >
                                            <div className="exp-radio-circle">
                                                {paymentMethod === 'Stripe' && <div className="exp-radio-dot"></div>}
                                            </div>
                                            <div className="exp-payment-info">
                                                {/* <img 
                                                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/512px-Stripe_Logo%2C_revised_2016.svg.png" 
                                                    alt="Stripe" 
                                                    className="stripe-logo" 
                                                /> */}
                                                <div className="exp-payment-text">
                                                    <h4>Pay with Stripe</h4>
                                                    <p>Secure payment processing with Stripe</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {paymentMethod === '3DSecure' && (
                                    <div className="exp-section">
                                        <h3 className="exp-section-title">
                                            <HiOutlineCreditCard />
                                            Payment Details
                                        </h3>
                                        <div className="exp-payment-form">
                                            <div className="exp-field">
                                                <label htmlFor="cardHolderName">Card Holder Name *</label>
                                                <input
                                                    type="text"
                                                    id="cardHolderName"
                                                    value={paymentDetails.cardHolderName}
                                                    onChange={(e) => setPaymentDetails({...paymentDetails, cardHolderName: e.target.value})}
                                                    placeholder="Full Name"
                                                />
                                            </div>
                                            
                                            <div className="exp-field">
                                                <label htmlFor="cardNumber">Card Number *</label>
                                                <input
                                                    type="text"
                                                    id="cardNumber"
                                                    value={paymentDetails.cardNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                                        const formatted = value.replace(/(.{4})/g, '$1 ').trim();
                                                        setPaymentDetails({...paymentDetails, cardNumber: formatted});
                                                    }}
                                                    placeholder="•••• •••• •••• ••••"
                                                    maxLength="19"
                                                />
                                            </div>
                                            
                                            <div className="exp-field-row">
                                                <div className="exp-field">
                                                    <label htmlFor="expiryDate">Expiry (MM/YY) *</label>
                                                    <input
                                                        type="text"
                                                        id="expiryDate"
                                                        value={paymentDetails.expiryDate}
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/[^0-9]/g, '');
                                                            if (value.length > 2) {
                                                                value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                                            }
                                                            setPaymentDetails({...paymentDetails, expiryDate: value});
                                                        }}
                                                        placeholder="MM / YY"
                                                        maxLength="5"
                                                    />
                                                </div>
                                                
                                                <div className="exp-field">
                                                    <label htmlFor="cvc">CVC *</label>
                                                    <div className="exp-cvc-wrapper">
                                                        <input
                                                            type="text"
                                                            id="cvc"
                                                            value={paymentDetails.cvc}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                setPaymentDetails({...paymentDetails, cvc: value});
                                                            }}
                                                            placeholder="•••"
                                                            maxLength="3"
                                                        />
                                                        <HiOutlineCreditCard />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Terms */}
                        <div className="exp-terms">
                            <input
                                type="checkbox"
                                id="termsAccepted"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                            />
                            <label htmlFor="termsAccepted" className="exp-terms-label">
                                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            </label>
                        </div>

                        {/* Pay Button */}
                        <button 
                            className="exp-pay-btn"
                            onClick={handlePayment}
                            disabled={!termsAccepted || isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing...
                                </>
                            ) : isFreeBooking
                                ? 'Confirm Free Booking'
                                : isFullyCoveredByBalance
                                    ? 'Confirm Booking (Pay with Balance)'
                                    : useBalance && balanceToUse > 0
                                        ? `Pay Remaining €${remainingToPay.toFixed(2)}`
                                        : 'Proceed to Payment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExperienceBookingDetails;
